import styled from "styled-components";
import { Link } from "react-router-dom";
import { colors, getTypographyFontStyle, device } from "../../styles/global.style";

const FooterContainer = styled.footer`
  background: ${colors.gradient};
  color: ${colors.light};
  margin-top: 80px;

  @media ${device.md} {
    margin-top: 120px;
  }
`;

const FooterTop = styled.div`
  padding: 48px 24px;

  @media ${device.md} {
    padding: 64px 48px 48px;
  }

  @media ${device.lg} {
    padding: 80px 64px 48px;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  max-width: 1400px;
  margin: 0 auto;

  @media ${device.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: 48px;
  }

  @media ${device.lg} {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: 64px;
  }
`;

const FooterBrand = styled.div`
  @media ${device.md} {
    grid-column: span 2;
  }

  @media ${device.lg} {
    grid-column: span 1;
  }
`;

const FooterLogo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 20px;
  filter: brightness(0) invert(1);

  @media ${device.md} {
    width: 140px;
    margin-bottom: 24px;
  }
`;

const FooterTagline = styled.p`
  ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
  color: rgba(248, 250, 252, 0.8);
  line-height: 1.7;
  max-width: 300px;
  margin-bottom: 24px;
`;

const FooterSection = styled.div``;

const FooterHeading = styled.h4`
  ${getTypographyFontStyle({ variant: "body1", weight: 600 })};
  color: ${colors.primary};
  margin-bottom: 20px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 30px;
    height: 2px;
    background: ${colors.solarGradient};
    border-radius: 1px;
  }

  @media ${device.md} {
    margin-bottom: 24px;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
  color: rgba(248, 250, 252, 0.85);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  position: relative;

  &::before {
    content: '→';
    opacity: 0;
    transform: translateX(-8px);
    transition: all 0.3s ease;
    color: ${colors.primary};
  }

  &:hover {
    color: ${colors.light};
    padding-left: 4px;

    &::before {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  ${getTypographyFontStyle({ variant: "small", weight: 400 })};

  img, span {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  span {
    font-size: 18px;
  }

  div {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  strong {
    ${getTypographyFontStyle({ variant: "small", weight: 600 })};
    color: ${colors.light};
    display: block;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    color: rgba(248, 250, 252, 0.8);
    line-height: 1.6;
  }

  a {
    color: rgba(248, 250, 252, 0.85);
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: ${colors.primary};
    }
  }

  @media ${device.md} {
    ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialIcon = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
    transition: transform 0.3s ease;
  }

  &:hover {
    background: ${colors.primary};
    border-color: ${colors.primary};
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(245, 166, 35, 0.3);

    img {
      transform: scale(1.1);
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(248, 250, 252, 0.2),
    transparent
  );
  margin: 0 24px;

  @media ${device.md} {
    margin: 0 48px;
  }
`;

const FooterBottom = styled.div`
  padding: 24px;
  text-align: center;

  @media ${device.md} {
    padding: 32px 48px;
  }
`;

const Copyright = styled.p`
  ${getTypographyFontStyle({ variant: "small", weight: 400 })};
  color: rgba(248, 250, 252, 0.6);
  margin: 0;

  @media ${device.md} {
    ${getTypographyFontStyle({ variant: "body2", weight: 400 })};
  }
`;

export {
  FooterContainer,
  FooterTop,
  FooterBottom,
  FooterGrid,
  FooterBrand,
  FooterLogo,
  FooterTagline,
  FooterSection,
  FooterHeading,
  FooterLinks,
  FooterLink,
  ContactGrid,
  ContactItem,
  SocialLinks,
  SocialIcon,
  Divider,
  Copyright,
};
